import React, { useState, useEffect, CSSProperties } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { HiOutlineArrowLeft, HiCurrencyDollar, HiXCircle } from 'react-icons/hi'
import './PlanDetailsPage.scss'
import logoImage_1 from '../../assets/White PNG 1.png'
import logoImage_2 from '../../assets/Black PNG 1.png'
import RulesModal from '../PaymentModal/RulesModal'
import PaymentModal from '../PaymentModal/PaymentModal'

const ConfirmPurchasePage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const theme = queryParams.get('theme') || 'dark'
  const planId = parseInt(queryParams.get('planId') || '0')
  const planName = queryParams.get('planName') || 'نامشخص'
  const planDuration = parseInt(queryParams.get('planDuration') || '0')
  const periodLabel = queryParams.get('periodLabel') || 'نامشخص'
  const tradeAccountId = parseInt(queryParams.get('accountId') || '0')
  const accountNumber = queryParams.get('accountNumber') || 'نامشخص'
  const accountServer = queryParams.get('accountServer')
    ? decodeURIComponent(queryParams.get('accountServer') as string)
    : 'نامشخص'

  const [calculatedFinalPrice, setCalculatedFinalPrice] = useState<number | null>(null)
  const [fetchedAccountValue, setFetchedAccountValue] = useState<number | null>(null)
  const [isPriceLoading, setIsPriceLoading] = useState<boolean>(true)
  const [priceError, setPriceError] = useState<string | null>(null)
  const [showRulesModal, setShowRulesModal] = useState<boolean>(false)
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false)
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string | null>(null)
  const [paymentError, setPaymentError] = useState<string | null>(null)

  const [discountCode, setDiscountCode] = useState<string>('')
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string | null>(null)
  const [isDiscountLoading, setIsDiscountLoading] = useState<boolean>(false)
  const [discountError, setDiscountError] = useState<string | null>(null)

  const fetchPrice = async (code: string | null = null) => {
    const setLoading = code ? setIsDiscountLoading : setIsPriceLoading
    setLoading(true)
    setPriceError(null)
    setDiscountError(null)

    const token = localStorage.getItem('token')
    if (!token) {
      setPriceError('توکن احراز هویت یافت نشد.')
      setLoading(false)
      return
    }
    try {
      const payload: { [key: string]: string } = {
        plan_id: planId.toString(),
        trade_account_id: tradeAccountId.toString(),
        plan_duration: planDuration.toString()
      }
      if (code) {
        payload['discount_code'] = code
      }

      const response = await fetch(
        'https://api.neontradepro.com/plan/price?' + new URLSearchParams(payload).toString(),
        { method: 'GET', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      )
      if (!response.ok) throw new Error((await response.json()).detail || 'خطا در محاسبه قیمت.')
      const data = await response.json()

      setCalculatedFinalPrice(data.price / 100)
      setFetchedAccountValue(data.balance / 100)

      if (code) {
        setAppliedDiscountCode(code)
      }
    } catch (err: any) {
      if (code) {
        setDiscountError(err.message)
        setAppliedDiscountCode(null)
      } else {
        setPriceError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (planId && tradeAccountId && planDuration) {
      fetchPrice()
    }
  }, [planId, tradeAccountId, planDuration])

  const handleApplyDiscount = () => {
    if (!discountCode.trim()) {
      setDiscountError('لطفاً کد تخفیف را وارد کنید.')
      return
    }
    fetchPrice(discountCode)
  }

  const handleRemoveDiscount = () => {
    setDiscountCode('')
    setAppliedDiscountCode(null)
    setDiscountError(null)
    fetchPrice()
  }

  // Note: invoice will be created when user presses "پرداخت" inside PaymentModal.
  // This function only opens the payment modal after rules confirmation.
  const handleBuyPlanClick = async () => {
    setPaymentError(null)
    setCurrentInvoiceId(null)
    setShowPaymentModal(true)
  }

  const handleClosePaymentModal = (status?: 'success' | 'cancel') => {
    // Always close the payment modal UI
    setShowPaymentModal(false)
    // Only navigate to plan-settings when user explicitly confirmed success
    if (status === 'success' && currentInvoiceId) {
      navigate(
        `/plan-settings?theme=${theme}&planId=${planId}&price=${
          calculatedFinalPrice || 0
        }&periodLabel=${encodeURIComponent(periodLabel)}&accountValue=${
          fetchedAccountValue || 0
        }&planName=${encodeURIComponent(
          planName
        )}&planDuration=${planDuration}&invoiceId=${currentInvoiceId}&accountId=${tradeAccountId}&accountNumber=${encodeURIComponent(
          accountNumber
        )}&accountServer=${encodeURIComponent(accountServer)}`
      )
    }
    // If cancelled or closed, do nothing further — remain on the Confirm Purchase page
  }
  const pageHeaderStyle: CSSProperties = {
    padding: '0.75rem 2rem',
    borderBottom: '1px solid var(--border-color, #333)',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backgroundColor: theme === 'dark' ? '#121212' : '#f0f0f0',
    direction: 'rtl'
  }

  const logoInHeaderStyle: CSSProperties = {
    height: '30px',
    display: 'block'
  }

  const handleBack = () => {
    navigate(`/add-account?${queryParams.toString()}`)
  }

  return (
    <>
      <header style={pageHeaderStyle}>
        <button onClick={handleBack} className='details-back-link' style={{ marginRight: '1rem' }}>
          <HiOutlineArrowLeft /> بازگشت
        </button>
        <div className='logo'>
          <Link to={`/?theme=${theme}`}>
            <img src={theme === 'dark' ? logoImage_1 : logoImage_2} alt='لوگوی نئون' style={logoInHeaderStyle} />
          </Link>
        </div>
      </header>
      <div className={`plan-details-page ${theme}`}>
        <div className='details-container'>
          <div className='details-header'>
            <h1> تایید نهایی و پرداخت</h1>
          </div>
          <div
            className='plan-summary'
            style={{
              marginBottom: '2rem',
              padding: '1.5rem',
              background: 'var(--pdp-feature-card-bg)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <p>
                <strong>پلن:</strong> {planName}
              </p>
              <p>
                <strong>دوره:</strong> {periodLabel}
              </p>
              <p>
                <strong>شماره حساب:</strong> {accountNumber} ({accountServer})
              </p>
              <p>
                <strong>موجودی حساب:</strong>{' '}
                {isPriceLoading
                  ? '...'
                  : typeof fetchedAccountValue === 'number'
                  ? `$${fetchedAccountValue.toLocaleString('en-US')}`
                  : 'نامشخص'}
              </p>
            </div>

            <div style={{ margin: '1.5rem 0' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type='text'
                  placeholder='کد تخفیف'
                  value={discountCode}
                  onChange={e => setDiscountCode(e.target.value)}
                  disabled={!!appliedDiscountCode}
                  style={{
                    flexGrow: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--input-background)',
                    color: 'var(--text-color)'
                  }}
                />
                {!appliedDiscountCode ? (
                  <button
                    onClick={handleApplyDiscount}
                    disabled={isDiscountLoading}
                    className='select-plan-btn-details'
                    style={{
                      padding: '10px 15px',
                      minWidth: '80px',
                      background: 'linear-gradient(to right, hsl(199, 100%, 50%), hsl(324, 100%, 50%))',
                      fontSize: '0.9rem'
                    }}
                  >
                    {isDiscountLoading ? '...' : 'اعمال'}
                  </button>
                ) : (
                  <button
                    onClick={handleRemoveDiscount}
                    className='select-plan-btn-details'
                    style={{
                      padding: '10px 15px',
                      minWidth: '80px',
                      fontSize: '0.9rem',
                      background: 'var(--pdp-error-text)'
                    }}
                  >
                    <HiXCircle />
                  </button>
                )}
              </div>
              {discountError && (
                <p style={{ color: 'var(--pdp-error-text)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  {discountError}
                </p>
              )}
              {appliedDiscountCode && (
                <p style={{ color: 'var(--pdp-success-text, #28a745)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  کد "{appliedDiscountCode}" با موفقیت اعمال شد.
                </p>
              )}
            </div>

            <hr style={{ margin: '1rem 0', borderColor: 'var(--border-color)' }} />
            <div className='plan-price-display-details' style={{ marginTop: '1rem', justifyContent: 'center' }}>
              {isPriceLoading ? (
                <span className='price-placeholder'>در حال محاسبه قیمت...</span>
              ) : priceError ? (
                <span className='price-placeholder' style={{ color: 'var(--pdp-error-text)' }}>
                  {priceError}
                </span>
              ) : typeof calculatedFinalPrice === 'number' ? (
                <>
                  <span style={{ fontSize: '1.2rem', alignSelf: 'center', marginRight: '1rem' }}>مبلغ نهایی:</span>
                  <span className='price-currency'>$</span>
                  <span className='price-amount'>{calculatedFinalPrice.toLocaleString('en-US')}</span>
                </>
              ) : (
                <span className='price-placeholder'>قیمت نامشخص</span>
              )}
            </div>
            <p
              style={{ fontSize: '0.8rem', color: 'var(--text-muted-color)', textAlign: 'center', marginTop: '0.5rem' }}
            >
              قیمت نهایی بر اساس موجودی حساب و کد تخفیف محاسبه شده است.
            </p>
            <p
              style={{ fontSize: '0.8rem', color: 'red', textAlign: 'center', marginTop: '0.5rem', border: '1px solid red', padding: '0.5rem',borderRadius: '8px' }}
            >
              ⚠️ هشدار مهم لطفاً هنگام واریز، کارمزد شبکه و کارمزد صرافی مبدأ را به مبلغ فاکتور اضافه کنید تا دقیقاً
              مبلغ مورد نیاز در کیف‌پول مقصد ثبت شود. مسئولیت واریز صحیح بر عهده کاربر است. هرگونه واریز کمتر یا بیشتر
              از مبلغ اعلام‌شده موجب تأخیر در تأیید و نیاز به ثبت تیکت پشتیبانی خواهد شد.{' '}
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
            <button
              onClick={() => setShowRulesModal(true)}
              className='select-plan-btn-details'
              disabled={
                isPriceLoading || isDiscountLoading || priceError !== null || typeof calculatedFinalPrice !== 'number'
              }
              style={{
                minWidth: '300px',
                background: 'linear-gradient(to right, hsl(199, 100%, 50%), hsl(324, 100%, 50%))',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              پرداخت نهایی
              <HiCurrencyDollar />
            </button>
          </div>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link to={`/add-account?${queryParams.toString()}`} className='details-back-link'>
              <HiOutlineArrowLeft /> بازگشت به مرحله قبل
            </Link>
          </div>
        </div>
      </div>
      {paymentError && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            background: 'red',
            color: 'white',
            padding: '1rem',
            borderRadius: '8px'
          }}
        >
          {paymentError}
        </div>
      )}
      <RulesModal
        isOpen={showRulesModal}
        onClose={() => setShowRulesModal(false)}
        onConfirm={async () => {
          // Close rules modal first, then open the payment modal.
          setShowRulesModal(false)
          await handleBuyPlanClick()
        }}
        planDetails={{ name: planName, price: calculatedFinalPrice || 0, period: periodLabel }}
        theme={theme}
      />
      {showPaymentModal && (
        <PaymentModal
          plan={{ id: planId, name: planName, price: calculatedFinalPrice || 0, period: periodLabel }}
          onClose={(status) => handleClosePaymentModal(status)}
          invoiceId={currentInvoiceId}
          planDuration={planDuration}
          tradeAccountId={tradeAccountId}
          discountCode={appliedDiscountCode}
          theme={theme}
        />
      )}
    </>
  )
}
export default ConfirmPurchasePage
